import React, { Fragment, ReactChild } from "react";

import classnames from "classnames";
import { Transition } from "@headlessui/react";

import { useWombatTheme } from "../../theme";
import Button from "../Button";
import { CloseIcon } from "../../../icons";

export type SlideoverScale = "base" | "lg";

export interface SlideoverProps extends React.ComponentPropsWithoutRef<"div"> {
  open: boolean;
  onClose: () => void;
  scale?: SlideoverScale;
  title: string;
  description?: string;
  children?: ReactChild;
}

const Slideover = React.forwardRef<HTMLDivElement, SlideoverProps>(
  (
    {
      open,
      onClose,
      scale = "base",
      title,
      description,
      children,
      ...divHTMLProps
    },
    ref
  ) => {
    const { theme } = useWombatTheme();

    const containerClassName = classnames(
      theme.layout.slideover.container[scale]
    );
    const titleContainerClassName = classnames(
      theme.layout.slideover.titleContainer
    );
    const titleClassName = classnames(theme.layout.slideover.title);
    const descriptionClassName = classnames(theme.layout.slideover.description);

    return (
      <Transition.Root show={open} as={Fragment} unmount={false}>
        <div
          className="fixed inset-0 z-40 overflow-hidden"
          ref={ref}
          {...divHTMLProps}
        >
          <div className="absolute inset-0 overflow-hidden">
            <div role="button" onClick={onClose} className="absolute inset-0" data-test-id="slideoverToggle">
              <Transition.Child
                enter="transition-opacity ease-linear duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity ease-linear duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                className="absolute inset-0"
              >
                <div className="absolute inset-0 bg-black opacity-60" />
              </Transition.Child>
            </div>
            <div className="fixed inset-y-0 right-0 z-50 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                unmount={false}
                enter="transform transition ease-in-out duration-200"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-200"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <div className={containerClassName}>
                  <div className="flex flex-col h-full overflow-y-scroll bg-white shadow-xl">
                    <div className={titleContainerClassName}>
                      <div className="flex items-center justify-between">
                        <div className={titleClassName}>{title}</div>
                        <div className="flex items-center ml-3 h-7">
                          <Button
                            scale="lg"
                            variant="ghost"
                            rounded
                            icon={CloseIcon}
                            onClick={onClose}
                          >
                            <span className="sr-only">Close panel</span>
                          </Button>
                        </div>
                      </div>
                      {description && (
                        <div className="mt-1">
                          <p className={descriptionClassName}>{description}</p>
                        </div>
                      )}
                    </div>
                    <div className="relative flex-1">{children}</div>
                  </div>
                </div>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Transition.Root>
    );
  }
);

export default React.memo(Slideover);
